<template>
  <div class="satellite-tracker" style="padding: 0;">
    <div class="map-container" id="map-container"></div>
    <div class="params-card">
      <p>经度: {{ this.longitude }}</p>
      <p>纬度: {{ this.latitude }}</p>
      <p>高度: {{ this.height }}</p>
    </div>
  </div>
</template>

<script>
import L from "leaflet";
import {
  twoline2satrec,
  propagate,
  gstime,
  eciToGeodetic,
  degreesLong,
  degreesLat,
  sgp4,
} from "satellite.js";

export default {
  name: "satellite-track",
  props: ["tles"],
  data() {
    return {
      map: null,
      OSMUrl: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      // tles: null,
      TLE_DATA_DATE: new Date("2020-06-02T23:36:30.924288+08:00").getTime(),
      activeClock: null,
      satellitesLayer: null,
      satrec: null,
      orbitSingleLine: {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: []
        }
      },
      orbitLine: {
        type: "MultiLineString",
        coordinates: [[], []],
      },
      orbitLine1: [
        {
          type: "LineString",
          coordinates: [],
        },
      ],
      orbitLine2: [
        {
          type: "LineString",
          coordinates: [],
        },
      ],
      orbitLineStyle: {
        color: "#ff7800",
        weight: 2,
        opacity: 0.75,
      },
      params: {
        longitude: "",
        latitude: "",
        height: 0,
      },
      satIcon: null,
    };
  },
  computed: {
    longitude: {
      get() {
        return Math.round(degreesLong(this.params.longitude) * 100) / 100;
      },
    },
    latitude: {
      get() {
        return Math.round(degreesLat(this.params.latitude) * 100) / 100;
      },
    },
    height: {
      get() {
        return Math.round(this.params.height * 100) / 100;
      },
    },
  },
  methods: {
    /**
     * Backend Service API Func Wrapper
     */
    /**
     * Phy/Math Formula
     */
    // Get orbital period in minutes
    orbitalPeriod(position, velocity) {
      const EARTH_RADIUS_KM = 6371;
      const GRATIVATIONAL_EARTH_PARAMETER = 3.986004418 * Math.pow(10, 14);
      const { x, y, z } = position;
      const { x: vx, y: vy, z: vz } = velocity;

      const positionVectorMagnitude = Math.sqrt(Math.pow(x, 2) + Math.pow(y, 2) + Math.pow(z, 2));
      const velocityVectorMagnitude = Math.sqrt(Math.pow(vx, 2) + Math.pow(vy, 2) + Math.pow(vz, 2));

      const semiMajorAxis =
        positionVectorMagnitude / (1 - Math.pow(velocityVectorMagnitude, 2) * EARTH_RADIUS_KM / GRATIVATIONAL_EARTH_PARAMETER);

      const orbitalPeriod = 2 * Math.PI * Math.pow(semiMajorAxis * 1000, 1.5) / Math.sqrt(GRATIVATIONAL_EARTH_PARAMETER);

      return orbitalPeriod / 60;
    },
    /**
     * UI Control
     */
    calOrbitLine(satrec) {
      let preLongitude;
      // const cycle = parseFloat(this.period);
      const now = new Date();

      const pav = propagate(satrec, now)
      const cycle = this.orbitalPeriod(pav.position, pav.velocity)

      const startTime = new Date(now.getTime() - (cycle / 3) * 60 * 1000); // 当前时间往前1/3个轨道周期的起点
      const endTime = new Date(now.getTime() + (2 * cycle / 3) * 60 * 1000); // 当前时间2/3个轨道周期的终点
      const sampleRate = cycle * 60 / 200; // // 采样频率，单位为秒钟
      const incrementMs = sampleRate * 1000; // 递增时间，单位为毫秒
      const inclination = this.radiansToDegrees(satrec.inclo)


      for (let time = startTime; time <= endTime; time.setTime(time.getTime() + incrementMs)) {
        const positionAndVelocity = propagate(satrec, time);
        const gmst = gstime(time);
        const positionEci = positionAndVelocity.position;
        const positionGd = eciToGeodetic(positionEci, gmst);
        const lon = this.radiansToDegrees(positionGd.longitude);
        const lat = this.radiansToDegrees(positionGd.latitude);
        const alt = positionGd.height;

        preLongitude = !preLongitude ? lon : preLongitude;
        if (inclination < 90 && lon >= preLongitude) {
          this.orbitLine.coordinates[0].push([lon, lat, alt]);
        } else if (inclination < 90 && lon < preLongitude) {
          this.orbitLine.coordinates[1].push([lon, lat, alt]);
        }
        if (inclination > 90 && lon <= preLongitude) {
          this.orbitLine.coordinates[0].push([lon, lat, alt]);
        } else if (inclination > 90 && lon > preLongitude) {
          this.orbitLine.coordinates[1].push([lon, lat, alt]);
        }

        this.orbitSingleLine.geometry.coordinates.push([lon, lat, alt]);
      }
      this.orbitLine.coordinates[0].sort((a, b) => a[0] - b[0]);
      this.orbitLine.coordinates[1].sort((a, b) => a[0] - b[0]);
      L.geoJSON(this.orbitLine, { style: this.orbitLineStyle }).addTo(this.map);

      // this.orbitSingleLine.geometry.coordinates.sort((a, b) => a[0] - b[0]);
      // L.geoJSON(this.orbitSingleLine, { style: this.orbitLineStyle }).addTo(this.map);
    },
    /**
     * Factory function for keeping track of elapsed time and rates.
     */
    // 模拟时间流速控制
    clock() {
      let rate = 60; // 1ms elapsed : 60sec simulated
      let date = Date.now();
      let elapsed = 0;

      function clock() { }

      clock.date = function (timeInMs) {
        if (!arguments.length) return date + elapsed * rate;
        date = timeInMs;
        return clock;
      };

      clock.elapsed = function (ms) {
        if (!arguments.length) return date - Date.now(); // calculates elapsed
        elapsed = ms;
        return clock;
      };

      clock.rate = function (secondsPerMsElapsed) {
        if (!arguments.length) return rate;
        rate = secondsPerMsElapsed;
        return clock;
      };

      return clock;
    },
    draw(elapsed) {
      const dateInMs = this.activeClock.elapsed(elapsed).date();
      const date = new Date(dateInMs);
      this.satellitesLayer.clearLayers();
      const feature = this.satrecToFeature(this.satrec, date)
      this.satellitesLayer.addData(feature);
      this.map.attributionControl.setPrefix(date);
      this.map.panTo(
        new L.LatLng(
          feature.geometry.coordinates[1],
          feature.geometry.coordinates[0]
        )
      );
      window.requestAnimationFrame(this.draw);
    },
    /**
     * Conversion
     */
    radiansToDegrees(radians) {
      return (radians * 180) / Math.PI;
    },
    // Parses text file string of tle into groups.
    // @return {Array<string[]>} Like [['tle line 1', 'tle line 2'], ...]
    parseTle(lines) {
      // remove last newline so that we can properly split all the lines
      // const lines = threeleString.replace(/\r?\n$/g, "").split(/\r?\n/);

      return lines.reduce(function (acc, cur, index) {
        if (index % 2 === 0) acc.push([]);
        acc[acc.length - 1].push(cur);
        return acc;
      }, []);
    },
    satrecToFeature(satrec, date, props) {
      let properties = props || {};
      const positionAndVelocity = propagate(satrec, date);
      const gmst = gstime(date);
      const positionEci = positionAndVelocity.position;

      const positionGd = eciToGeodetic(positionEci, gmst);

      const lon = degreesLong(positionGd.longitude);
      const lat = degreesLat(positionGd.latitude);
      const alt = positionGd.height;

      Object.assign(this.params, positionGd);
      return {
        type: "Feature",
        properties: properties,
        geometry: {
          type: "Point",
          coordinates: [lon, lat, alt],
        },
      };
    },
    /**
     * MAIN PART
     */
    update(twoLineTLE) {
      this.satrec = twoline2satrec(...twoLineTLE);
      this.calOrbitLine(this.satrec)
      this.activeClock = this.clock().rate(1).date(new Date().getTime());
      const satIcon = L.icon({
        iconUrl: "img/icons/satellite-model.png",
        iconSize: [45, 45],
      });
      const feature = this.satrecToFeature(this.satrec, new Date(this.activeClock.date()));
      this.satellitesLayer = L.geoJSON(feature, {
        pointToLayer: function (geoJsonPoint, latlng) {
          return L.marker(latlng, { icon: satIcon });
        },
      });
      this.satellitesLayer.addTo(this.map);
      window.requestAnimationFrame(this.draw);
    }
  },
  mounted() {
    if (typeof this.tles === 'undefined' || this.tles === null) {
      return
    }
    this.map = this.$utils.map.createMap("map-container", {
      zoom: 3,
      minZoom: 1,
      maxZoom: 10,
      center: [0, 0],
      dragging: false,
      maxBounds: L.latLngBounds(L.latLng(-90, -180), L.latLng(90, 180)),
    });
    // 加载 open street map 图层服务
    this.$utils.map.createTileLayer(this.map, 'https://s{s}.tiles.vsat.cloud/tile/vec/{z}/{y}/{x}', { subdomains: [0, 1, 2, 3] });
    this.$utils.map.createTileLayer(this.map, 'https://s{s}.tiles.vsat.cloud/tile/cva/{z}/{y}/{x}', { subdomains: [0, 1, 2, 3] });

    this.update([this.tles.tle_line1, this.tles.tle_line2]);
  },
};
</script>
<style lang="stylus" scoped>
.satellite-tracker {
  position: relative;
}

.map-container {
  // position: fixed;
  width: 100%;
  height: 50vh;
}

.params-card {
  position: absolute;
  float: right;
  z-index: 999;
  top: 5px;
  right: 10px;
  width: 90px;
  font-size: 12px;
}
</style>
